import React from 'react';
import PropTypes from 'prop-types';
import Form from '@ey/canvascoreservices/Form';
import Icon from '@ey/canvascoreservices/Icon';
import Modal from '@ey/canvascoreservices/Modal';
import ContentSwitcher from '@ey/canvascoreservices/ContentSwitcher';
import OutputText from '@ey/canvascoreservices/OutputText';
import Select from '@ey/canvascoreservices/Select';
import {MotifActionIcInfoOutline24px} from '@ey-xd/motif-icon';
import {MotifToggleSwitch} from '@ey-xd/motif-react';

import {GALinkStatus, UserLookupType, gaRoleTypes} from '../../../../../../../../../util/uiconstants';
import Utility from '../../../../../../../../../util/utilityfunctions';
import {NAME, SCOPE, GROUP_ROLE, REGION, EXTERNAL_USER, INTERNAL_USER, NOT_USING_CANVAS} from './utils/constants';
import {getFormattedComponents, getFormattedScopes} from './utils/getFormatted';
import normalizeInitialValues from './utils/normalizeInitialValues';
import {normalizeValues} from './utils/normalizeValues';

import HrAlt from '../../../../../../../../Common/Hr';
import ErrorSummary from '../../../../../../../ErrorSummary/ErrorSummary';
import LoadingIndicator from '../../../../../../../LoadingIndicator/LoadingIndicator';
import TextInput from '../FormComponents/TextInput';
import Tooltip from '../Tooltip';
import LinkInfo from './LinkInfo';
import StyledForm from './StyledForm';
import UserSearch from './UserSearch';

export const defaultValues = {
	[NAME]: '',
	[SCOPE]: '',
	[GROUP_ROLE]: 3,
	[REGION]: '',
	[INTERNAL_USER]: null,
	[EXTERNAL_USER]: null,
	[NOT_USING_CANVAS]: false
};

const ComponentEditor = ({
	initialValues,
	onSave,
	isLoading,
	setIsLoading,
	onHideModal,
	components,
	scopes,
	labels,
	showModal,
	modalHeaderText
}) => {
	const optionNotApplicable = labels.gaRegionTypesLabel;
	const normalizedNotApplicableOption = {
		value: optionNotApplicable.id,
		label: optionNotApplicable.displayName,
		title: optionNotApplicable.displayName
	};
	const normalizedDefaultValues = {
		...defaultValues,
		[REGION]: normalizedNotApplicableOption.value
	};

	const schema = {
		[NAME]: {required: true, maxLength: 500},
		[SCOPE]: {required: true},
		[GROUP_ROLE]: {required: true},
		[REGION]: {required: (values) => parseInt(values[GROUP_ROLE]) === gaRoleTypes.component},
		[INTERNAL_USER]: {required: false},
		[EXTERNAL_USER]: {required: false},
		[NOT_USING_CANVAS]: {required: false}
	};

	const normalizedInitialValues = normalizeInitialValues(
		normalizedDefaultValues,
		initialValues,
		gaRoleTypes,
		components,
		GALinkStatus,
		optionNotApplicable
	);

	const normalizedRegions = getFormattedComponents(components, optionNotApplicable, gaRoleTypes);

	const props = {
		initialValues: normalizedInitialValues,
		schema
	};

	if (onSave)
		props.onSubmit = (values, callback) => {
			onSave(normalizeValues(values, gaRoleTypes, optionNotApplicable))
				.then((hasError) => !hasError && callback())
				.catch(() => {
					setIsLoading(false);
				});
		};

	return (
		<Form {...props}>
			{({values, errors, handleChange, handleSubmit, handleReset, isFormValid}) => {
				const renderInstructional = () => {
					const textProps = {size: 'L', weight: 'light'};
					const desc = labels.createComponentInstructionalText;

					return (
						<div className="instructional">
							<OutputText {...textProps}>{Utility.createRichTextElement(desc)}</OutputText>
							<HrAlt />
						</div>
					);
				};

				const checkDisabledFieldStatus = () =>
					[GALinkStatus.Accepted, GALinkStatus.Sent].includes(initialValues?.linkRequest);

				const renderLinkInfo = () => {
					if (checkDisabledFieldStatus()) {
						return <LinkInfo labels={labels} />;
					}
				};

				const renderName = () => {
					const id = NAME;

					const props = {
						id,
						value: values[id],
						onChange: (value) => handleChange(id, value),
						label: labels.componentName,
						clearButtonTitle: labels.clearUpper,
						maxLength: 500,
						disabled: checkDisabledFieldStatus(),
						fixedErrorArea: false,
						wrapperClass: NAME,
						labelPosition: 'in'
					};

					if (errors[id]?.error) {
						props.error = labels.required || '';
					}

					return <TextInput {...props} />;
				};

				const renderScope = () => {
					const id = SCOPE;
					const props = {
						key: id,
						id: id,
						name: SCOPE,
						options: getFormattedScopes(scopes, null),
						value: values[id],
						disabled: checkDisabledFieldStatus(),
						onChange: (e) => handleChange(id, e),
						label: labels.scope,
						labelPosition: 'in'
					};

					if (errors[id]) {
						props.showErrorMessage = !!errors[id]?.error;
						props.errorMessage = labels.required || '';
					}

					return (
						<div className={SCOPE}>
							<Select {...props} />
						</div>
					);
				};

				const renderGroupRole = () => {
					const id = GROUP_ROLE;
					const props = {
						id,
						className: id
					};

					const onClick = (option) => {
						const newGroupRole = option.id;
						const newRegion = newGroupRole === gaRoleTypes.component ? normalizedNotApplicableOption.value : null;
						handleChange(id, newGroupRole, {[REGION]: newRegion});
					};

					const isDisabled = checkDisabledFieldStatus();

					const options = labels.contentSwitcher.filter((c) => c.id > 1).map((o) => ({id: o.id, label: o.displayName}));

					const switcherProps = {
						id: `${GROUP_ROLE}Switcher`,
						options,
						disabled: isDisabled,
						onChange: onClick,
						value: {id: values[id]}
					};

					return (
						<div {...props}>
							<OutputText body2>{labels.role}</OutputText>
							<ContentSwitcher {...switcherProps} />
						</div>
					);
				};

				const renderRegion = () => {
					const id = REGION;
					const isDisabled = values[GROUP_ROLE] !== gaRoleTypes.component || checkDisabledFieldStatus();

					const props = {
						key: id,
						id: id,
						name: REGION,
						options: normalizedRegions,
						value: values[id],
						disabled: isDisabled,
						onChange: (e) => handleChange(id, e),
						label: labels.region,
						labelPosition: 'in'
					};

					if (errors[id]) {
						props.showErrorMessage = !!errors[id]?.error;
						props.errorMessage = labels.required || '';
					}
					return (
						<div className={id}>
							<Select {...props} />
						</div>
					);
				};

				const renderUserSearch = () => {
					const id = INTERNAL_USER;

					const classNameprops = {
						id,
						className: id
					};

					const isDisabled = values[NOT_USING_CANVAS] === true;
					const props = {
						label: values[id]?.value ? values[id]?.value : null,
						lookupType: UserLookupType.Internal,
						value: values[id]?.value ? values[id]?.value : null,
						onUserSelectionChange: (e) => handleChange(id, e),
						onUserClear: () => handleChange(id, null),
						menuPlacement: 'bottom',
						isDisabled: isDisabled,
						displayField: 'displayName',
						showDropArrowOnDisabled: false,
						key: `pointOfContact ${values[id]} ${values[INTERNAL_USER]}`,
						labelInternal: labels.nameOrEmail,
						isAresErrorIcon: false
					};

					if (errors[id] && !isDisabled) {
						props.showErrorMessage = !!errors[id]?.error;
						props.errorMessage = errors[id]?.message || '';
					}

					return (
						<div {...classNameprops}>
							<UserSearch {...props} />
						</div>
					);
				};

				const renderNotUsingCanvas = () => {
					const id = NOT_USING_CANVAS;

					const handlerNotUsingCanvas = (e) => {
						if (e.target.checked) {
							handleChange(INTERNAL_USER, null, {[id]: e.target.checked});
						} else {
							handleChange(id, e.target.checked);
						}
					};

					const props = {
						onLabel: labels.notUsingCanvas,
						disabled: checkDisabledFieldStatus(),
						checked: values[id],
						offLabel: '',
						onChange: (e) => handlerNotUsingCanvas(e),
						id: id
					};

					const tooltipProps = {
						trigger: <Icon src={MotifActionIcInfoOutline24px} solid />,
						allowClick: false,
						placement: 'bottom',
						title: labels.tooltipIcon,
						className: 'doble-modal-tooltip'
					};

					const outputTextprops = {
						size: 'M',
						maxWidth: '25',
						wordBreak: true
					};

					return (
						<div className={id}>
							<MotifToggleSwitch {...props} />
							<Tooltip {...tooltipProps}>
								<OutputText {...outputTextprops}>{Utility.createRichTextElement(labels.tooltipIcon)}</OutputText>
							</Tooltip>
						</div>
					);
				};

				const modalProps = {
					show: showModal,
					headerText: modalHeaderText,
					labels: {
						modalConfirmBtnLabel: labels.saveAndClose,
						modalCancelBtnLabel: labels.modalCancelBtnLabel,
						modalCloseBtnTitletip: labels.modalCloseBtnTitletip
					},
					middleButtonTitle: labels.saveAndCreateAnother,
					noPaddingOnBody: true,
					modalsize: 'medium',
					fullHeight: false,
					showFooter: true,
					showFooterDivider: true,
					hideMiddleButton: initialValues !== undefined,
					isLoading,
					onClose: onHideModal,
					onOkClick: () => !isLoading && handleSubmit(() => onHideModal()),
					onMiddleButtonClick: () => !isLoading && handleSubmit(() => handleReset())
				};

				const hasInstructional = () => !initialValues || checkDisabledFieldStatus();

				return (
					<Modal {...modalProps}>
						<ErrorSummary />
						{isLoading && <LoadingIndicator view="inlineView" />}
						<StyledForm className="component-editor-form" showInstructional={hasInstructional()}>
							{!initialValues && renderInstructional()}
							{renderLinkInfo()}
							{renderName()}
							{renderScope()}
							{renderGroupRole()}
							{renderRegion()}
							{renderUserSearch()}
							{renderNotUsingCanvas()}
						</StyledForm>
					</Modal>
				);
			}}
		</Form>
	);
};

ComponentEditor.propTypes = {
	initialValues: PropTypes.shape({
		[NAME]: PropTypes.string.isRequired,
		[SCOPE]: PropTypes.shape({}).isRequired,
		[GROUP_ROLE]: PropTypes.number.isRequired,
		[REGION]: PropTypes.number.isRequired,
		[INTERNAL_USER]: PropTypes.string,
		[EXTERNAL_USER]: PropTypes.string,
		[NOT_USING_CANVAS]: PropTypes.bool.isRequired
	}),
	onSave: PropTypes.func,
	isLoading: PropTypes.bool,
	setIsLoading: PropTypes.func
};

ComponentEditor.defaultProps = {
	onSave: null,
	isLoading: false,
	setIsLoading: null
};

export default ComponentEditor;
